import { useAuth } from "../auth/AuthProvider";
import type { NavItem } from "./nav.config";
import { NAV_ITEMS } from "./nav.config";
import { isEmeisEnabled } from "../utils/emeisFlag";
import {
  isEmeisBulkEnabled,
  isEmeisCalendarEnabled,
  isEmeisClauseEnabled,
  isEmeisMonitorEnabled,
  isEmeisReceivableEnabled,
  isEmeisSignatureEnabled,
  isEmeisSubscriptionEnabled,
} from "../utils/emeisModuleFlags";
import { isEmeisAIEnabled } from "../utils/emeisAIFlag";
import { AllActions, AllModules } from "../accessManagement/AccessValues";

/* Generic, permission-driven nav filtering — an item with no `moduleName`
 * is treated as ungated (e.g. the dashboards); one with a `moduleName` is
 * shown only if the user has at least one action-permission for that
 * module (isModuleVisible), which itself already grants everything to
 * isAdmin (org admin) and isAppAdmin (super admin) users. A future module
 * only needs its nav entries tagged with `moduleName` — no changes needed
 * here.
 */
/**
 * High-priority core modules that should always remain visible in the sidebar.
 * Access/Permissions are handled inside ProtectedRoute (rendering ModuleDisabled page).
 */
const ALWAYS_VISIBLE_MODULES: number[] = [
  AllModules.Contract,
  AllModules.Obligation,
  AllModules.Receivable,
];

export function useFilteredNavItems() {
  const { user, hasPermission } = useAuth();

  const isAdminUser = !!(user?.isAdmin || user?.isAppAdmin);
  const emeisOn = isEmeisEnabled();
  const receivableOn = isEmeisReceivableEnabled();
  const clauseOn = isEmeisClauseEnabled();
  const calendarOn = isEmeisCalendarEnabled();
  const subscriptionOn = isEmeisSubscriptionEnabled();
  const aiOn = isEmeisAIEnabled();
  const bulkOn = isEmeisBulkEnabled();
  const signatureOn = isEmeisSignatureEnabled();
  const monitorOn = isEmeisMonitorEnabled();

  const filterItems = (items: NavItem[]): NavItem[] => {
    return items
      .map((item) => {
        // Product areas hidden from customers for now; back with the
        // internal "isEmeis" switch (utils/emeisFlag).
        if (item.emeisOnly && !emeisOn) return null;
        if (item.emeisReceivableOnly && !receivableOn) return null;
        if (item.emeisClauseOnly && !clauseOn) return null;
        if (item.emeisCalendarOnly && !calendarOn) return null;
        if (item.emeisSubscriptionOnly && !subscriptionOn) return null;
        if (item.emeisAiOnly && !aiOn) return null;
        if (item.emeisBulkOnly && !bulkOn) return null;
        if (item.emeisSignatureOnly && !signatureOn) return null;
        if (item.emeisMonitorOnly && !monitorOn) return null;
        if (item.adminOnly && !isAdminUser) {
          // A non-admin keeps only the children opened to module users
          // that they hold the permission for; none, and the group goes.
          const open = (item.children ?? []).filter(
            (c) =>
              c.nonAdminAllowed &&
              c.moduleName !== undefined &&
              hasPermission(c.moduleName, c.action ?? AllActions.SEARCH),
          );
          return open.length > 0 ? { ...item, children: open } : null;
        }
        if (item.superAdminOnly && !user?.isAppAdmin) return null;

        const isAlwaysVisible =
          item.moduleName !== undefined &&
          ALWAYS_VISIBLE_MODULES.includes(Number(item.moduleName));

        // No plan gate here, for core modules and Library sections alike:
        // every entry stays in the menu, and a click on one the plan leaves
        // out lands on the Module Disabled page, which sells the module and
        // offers the upgrade. Hiding it would leave the reader unaware the
        // feature exists.
        if (user?.isAdmin) {
          const filteredChildren = item.children
            ? filterItems(item.children)
            : undefined;
          return { ...item, children: filteredChildren };
        }

        if (item.moduleName && !isAlwaysVisible) {
          const requiredAction = item.action ?? AllActions.SEARCH;
          if (!hasPermission(item.moduleName, requiredAction)) {
            return null;
          }
        }

        let filteredChildren: NavItem[] | undefined;
        if (item.children) {
          filteredChildren = filterItems(item.children);
        }

        return {
          ...item,
          children: filteredChildren,
        };
      })
      .filter(Boolean) as NavItem[];
  };

  return filterItems(NAV_ITEMS);
}
